'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface GalleryImage {
  id: number;
  src: string;
  alt: string;
  category: string;
  title: string;
}

interface PhotoGalleryProps {
  className?: string;
}

const PhotoGallery = ({ className = '' }: PhotoGalleryProps) => {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);

  const categories = [
  { id: 'all', label: 'All Photos' },
  { id: 'systems', label: 'Hydroponic Systems' },
  { id: 'students', label: 'Students at Work' },
  { id: 'harvest', label: 'Harvest & Produce' }];


  const images: GalleryImage[] = [
  {
    id: 1,
    src: "https://images.unsplash.com/photo-1635986058216-bd9569444167",
    alt: 'Close-up of vibrant green lettuce growing in white PVC hydroponic system with nutrient solution visible',
    category: 'systems',
    title: 'NFT Hydroponic System'
  },
  {
    id: 2,
    src: "/assets/images/Screenshot_2026-02-05_at_5.48.24_PM-1770331828932.png",
    alt: 'Classroom photo showing teacher presenting Lab No.2 lesson with students working on laptops',
    category: 'systems',
    title: 'Lab No.2 Hydroponic Lesson'
  },
  {
    id: 3,
    src: "https://images.unsplash.com/photo-1716786134442-3116f11d3bb0",
    alt: 'Fresh harvested red and green lettuce heads arranged on wooden table with water droplets',
    category: 'harvest',
    title: 'Weekly Harvest'
  },
  {
    id: 4,
    src: "/assets/images/Screenshot_2026-02-05_at_5.49.34_PM-1770331894152.png",
    alt: 'Students posing with white hydroponic tower systems in classroom setting',
    category: 'systems',
    title: 'Students with Hydroponic Towers'
  },
  {
    id: 5,
    src: "/assets/images/Screenshot_2026-02-05_at_5.51.51_PM-1770332031102.png",
    alt: 'Students working with electronics and programming components in classroom',
    category: 'systems',
    title: 'Electronics and Programming'
  },
  {
    id: 6,
    src: "https://images.unsplash.com/photo-1592863689529-a0911c294bf9",
    alt: 'Multiple rows of green leafy vegetables growing in vertical hydroponic towers in bright classroom',
    category: 'systems',
    title: 'Vertical Tower Garden'
  },
  {
    id: 7,
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_11fa2201c-1769299748110.png",
    alt: 'Group of diverse middle school students smiling while working together on hydroponic system assembly',
    category: 'students',
    title: 'Team Collaboration'
  },
  {
    id: 9,
    src: "/assets/images/Screenshot_2026-02-01_at_12.27.19_PM-1769966850555.png",
    alt: 'Students working with 3D printing equipment and technology in classroom setting',
    category: 'students',
    title: 'Students at Work'
  },
  {
    id: 12,
    src: "/assets/images/Screenshot_2026-02-12_at_9.17.49_AM-1770905879760.png",
    alt: 'Students examining hydroponic tower systems in classroom',
    category: 'systems',
    title: 'Exploring Hydroponic Towers'
  },
  {
    id: 13,
    src: "/assets/images/Screenshot_2026-02-12_at_9.19.59_AM-1770906003797.png",
    alt: 'Student working on electronics and circuit board with laptop in classroom',
    category: 'students',
    title: 'Electronics & Circuit Board Work'
  },
  {
    id: 14,
    src: "/assets/images/Screenshot_2026-02-12_at_9.24.20_AM-1770906266392.png",
    alt: 'Group photo of students and instructors in classroom with electronics and tech materials',
    category: 'students',
    title: 'Classroom Learning Session'
  },
  {
    id: 15,
    src: "/assets/images/Screenshot_2026-02-12_at_9.40.15_AM-1770907224191.png",
    alt: 'Teacher presenting to students in classroom with projector overhead showing educational content',
    category: 'students',
    title: 'Interactive Classroom Presentation'
  }];


  const filteredImages = activeCategory === 'all' ?
  images :
  images.filter((img) => img.category === activeCategory);

  return (
    <section className={`py-20 bg-background ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">
            Program in Action
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Explore our photo gallery showcasing student projects, hydroponic systems, and harvest celebrations
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) =>
          <button
            key={category.id}
            onClick={() => setActiveCategory(category.id)}
            className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
            activeCategory === category.id ?
            'bg-brand-primary text-brand-primary-foreground shadow-brand-md' :
            'bg-surface text-text-secondary hover:bg-muted hover:text-text-primary'}`
            }>

              {category.label}
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredImages.map((image) =>
          <button
            key={image.id}
            onClick={() => setSelectedImage(image)}
            className="group relative h-64 rounded-xl overflow-hidden shadow-brand-sm hover:shadow-brand-lg transition-all duration-300 hover:-translate-y-1 bg-muted">

              <AppImage
              src={image.src}
              alt={image.alt}
              fill
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
              className="object-cover group-hover:scale-110 transition-transform duration-500" />

              <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal/80 via-brand-charcoal/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                <h3 className="text-white font-semibold">{image.title}</h3>
              </div>
            </button>
          )}
        </div>

        {selectedImage &&
        <div
          className="fixed inset-0 bg-brand-charcoal/95 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}>

            <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
              <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors duration-300"
              aria-label="Close image">

                <Icon name="XMarkIcon" size={24} />
              </button>

              <div className="relative h-[70vh] rounded-2xl overflow-hidden">
                <AppImage
                src={selectedImage.src}
                alt={selectedImage.alt}
                fill
                className="object-contain" />

              </div>

              <div className="mt-6 text-center">
                <h3 className="text-2xl font-bold text-white mb-2">{selectedImage.title}</h3>
                <p className="text-gray-300">{selectedImage.alt}</p>
              </div>
            </div>
          </div>
        }
      </div>
    </section>);

};

export default PhotoGallery;